import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Loader2, Pencil, Trash2, X, Search, Lock } from 'lucide-react';
import { activityTypesAPI } from '../utils/api';
import type { ActivityTypeResponse, ActivityTypeUpdate } from '../types/activityType';
import { ICON_NAMES, getIconByName } from '../hooks/useActivityTypes';
import ConfirmModal from '../components/ConfirmModal';
import { usePermissions } from '../hooks/usePermissions';

interface EditForm {
  id?: string;
  codice: string;
  label: string;
  icona: string;
  colore: string;
  attivo_lead: boolean;
  attivo_cliente: boolean;
  attivo_offerta: boolean;
  attivo_dashboard: boolean;
  ordine: number;
  attivo: boolean;
}

const emptyForm: EditForm = {
  codice: '',
  label: '',
  icona: ICON_NAMES[0] || '',
  colore: '#6b7280',
  attivo_lead: false,
  attivo_cliente: false,
  attivo_offerta: false,
  attivo_dashboard: true,
  ordine: 0,
  attivo: true,
};

const COLOR_PALETTE: string[] = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#3b82f6',
  '#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#6b7280',
];

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;
const ACTIVITY_TYPE_CODE_MAX_LENGTH = 50;

function activityTypeCodeFromLabel(label: string): string {
  return label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .slice(0, ACTIVITY_TYPE_CODE_MAX_LENGTH)
    .replace(/-+$/, "");
}

/** Piccolo switch pill riusato per i flag di contesto e "attivo" nella lista. */
function FlagToggle({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      title={label}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
        checked ? 'bg-blue-600' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

export default function ActivityTypes() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('activity_types:create');
  const canEdit = hasPermission('activity_types:edit');
  const canDelete = hasPermission('activity_types:delete');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<EditForm>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<ActivityTypeResponse | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['activity-types-admin', { page, search }],
    queryFn: async () => {
      const res = await activityTypesAPI.getTypes({
        page,
        limit: 30,
        search: search || undefined,
      });
      return res.data;
    },
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['activity-types-admin'] });
    queryClient.invalidateQueries({ queryKey: ['activity-types'] });
    queryClient.invalidateQueries({ queryKey: ['activity-analytics'] });
  };

  const saveMutation = useMutation({
    mutationFn: (values: EditForm) => {
      if (values.id) {
        const payload: ActivityTypeUpdate = {
          label: values.label,
          icona: values.icona || undefined,
          colore: values.colore || undefined,
          attivo_lead: values.attivo_lead,
          attivo_cliente: values.attivo_cliente,
          attivo_offerta: values.attivo_offerta,
          attivo_dashboard: values.attivo_dashboard,
          ordine: values.ordine,
          attivo: values.attivo,
        };
        return activityTypesAPI.updateType(values.id, payload);
      }
      return activityTypesAPI.createType({
        codice: values.codice,
        label: values.label,
        icona: values.icona || undefined,
        colore: values.colore || undefined,
        attivo_lead: values.attivo_lead,
        attivo_cliente: values.attivo_cliente,
        attivo_offerta: values.attivo_offerta,
        attivo_dashboard: values.attivo_dashboard,
        ordine: values.ordine,
        attivo: values.attivo,
      });
    },
    onSuccess: () => {
      invalidateAll();
      toast.success(form.id ? 'Tipologia aggiornata' : 'Tipologia creata');
      setIsModalOpen(false);
      setForm(emptyForm);
    },
    onError: (error: any) => {
      const status = error.response?.status;
      if (status === 409) {
        toast.error('Codice già usato in questo tenant');
      } else {
        toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
      }
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ActivityTypeUpdate }) =>
      activityTypesAPI.updateType(id, patch),
    onSuccess: () => invalidateAll(),
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => activityTypesAPI.deleteType(id),
    onSuccess: () => {
      invalidateAll();
      toast.success('Tipologia eliminata');
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    },
  });

  const types = data?.data || [];
  const pagination = data?.pagination;

  const openCreate = () => {
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = (t: ActivityTypeResponse) => {
    setForm({
      id: t.id,
      codice: t.codice,
      label: t.label,
      icona: t.icona || ICON_NAMES[0] || '',
      colore: t.colore || '#6b7280',
      attivo_lead: t.attivo_lead,
      attivo_cliente: t.attivo_cliente,
      attivo_offerta: t.attivo_offerta,
      attivo_dashboard: t.attivo_dashboard,
      ordine: t.ordine,
      attivo: t.attivo,
    });
    setIsModalOpen(true);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.codice.trim() || !form.label.trim()) {
      toast.error('Codice e label sono obbligatori');
      return;
    }
    if (form.colore && !HEX_COLOR_REGEX.test(form.colore)) {
      toast.error('Colore non valido (formato #RRGGBB)');
      return;
    }
    saveMutation.mutate(form);
  };

  const updateLabel = (label: string) => {
    setForm((current) => ({
      ...current,
      label,
      codice: current.id ? current.codice : activityTypeCodeFromLabel(label),
    }));
  };

  const PreviewIcon = getIconByName(form.icona);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tipologie Attività</h1>
          <p className="text-gray-600">
            Anagrafica dei tipi di attività per lead, clienti e offerte
          </p>
        </div>
        {canCreate && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-5 w-5" />
            Nuova tipologia
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca per codice o label..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : types.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>Nessuna tipologia</p>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-[1fr_repeat(5,88px)_88px] gap-2 px-6 py-2 text-xs font-medium text-gray-500 uppercase border-b border-gray-200">
              <span>Tipologia</span>
              <span className="text-center">Lead</span>
              <span className="text-center">Cliente</span>
              <span className="text-center">Offerta</span>
              <span className="text-center">Dashboard</span>
              <span className="text-center">Attivo</span>
              <span></span>
            </div>
            <div className="divide-y divide-gray-200">
              {types.map((t) => {
                const Icon = getIconByName(t.icona);
                const color = t.colore || '#6b7280';
                return (
                  <div
                    key={t.id}
                    className="grid grid-cols-1 md:grid-cols-[1fr_repeat(5,88px)_88px] gap-2 items-center px-6 py-4 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className="flex-shrink-0 p-1.5 rounded-lg"
                        style={{ backgroundColor: `${color}1A`, color }}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{t.label}</span>
                          {t.is_system && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500"
                              title="Tipo di sistema: generato dal codice, non eliminabile né disattivabile per contesto"
                            >
                              <Lock className="h-2.5 w-2.5" />
                              Sistema
                            </span>
                          )}
                        </div>
                        <code className="text-xs text-gray-400">{t.codice}</code>
                      </div>
                    </div>
                    <div className="flex md:justify-center">
                      <FlagToggle
                        label="Attivo per Lead"
                        checked={t.attivo_lead}
                        disabled={t.is_system || toggleMutation.isPending || !canEdit}
                        onChange={() =>
                          toggleMutation.mutate({ id: t.id, patch: { attivo_lead: !t.attivo_lead } })
                        }
                      />
                    </div>
                    <div className="flex md:justify-center">
                      <FlagToggle
                        label="Attivo per Cliente"
                        checked={t.attivo_cliente}
                        disabled={t.is_system || toggleMutation.isPending || !canEdit}
                        onChange={() =>
                          toggleMutation.mutate({ id: t.id, patch: { attivo_cliente: !t.attivo_cliente } })
                        }
                      />
                    </div>
                    <div className="flex md:justify-center">
                      <FlagToggle
                        label="Attivo per Offerta"
                        checked={t.attivo_offerta}
                        disabled={t.is_system || toggleMutation.isPending || !canEdit}
                        onChange={() =>
                          toggleMutation.mutate({ id: t.id, patch: { attivo_offerta: !t.attivo_offerta } })
                        }
                      />
                    </div>
                    <div className="flex md:justify-center">
                      <FlagToggle
                        label="Mostra nella dashboard"
                        checked={t.attivo_dashboard}
                        disabled={toggleMutation.isPending || !canEdit}
                        onChange={() => toggleMutation.mutate({ id: t.id, patch: { attivo_dashboard: !t.attivo_dashboard } })}
                      />
                    </div>
                    <div className="flex md:justify-center">
                      <FlagToggle
                        label="Attivo"
                        checked={t.attivo}
                        disabled={t.is_system || toggleMutation.isPending || !canEdit}
                        onChange={() =>
                          toggleMutation.mutate({ id: t.id, patch: { attivo: !t.attivo } })
                        }
                      />
                    </div>
                    <div className="flex items-center gap-1 justify-end md:justify-center">
                      {canEdit && (
                        <button
                          onClick={() => openEdit(t)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="Modifica"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => !t.is_system && setDeleteTarget(t)}
                          disabled={t.is_system}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          title={t.is_system ? 'I tipi di sistema non si possono eliminare' : 'Elimina'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} tipologie)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal create/edit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">
                {form.id ? 'Modifica tipologia' : 'Nuova tipologia'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Codice *
                  </label>
                  <input
                    type="text"
                    value={form.codice}
                    required
                    readOnly
                    maxLength={ACTIVITY_TYPE_CODE_MAX_LENGTH}
                    placeholder="es. sopralluogo"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {form.id
                      ? "Non modificabile dopo la creazione"
                      : "Generato dalla label (lettere, numeri e trattini; max 50)"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Label *
                  </label>
                  <input
                    type="text"
                    value={form.label}
                    onChange={(e) => updateLabel(e.target.value)}
                    required
                    maxLength={100}
                    placeholder="es. Sopralluogo"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icona</label>
                <div className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-lg">
                  {ICON_NAMES.map((name) => {
                    const IconOption = getIconByName(name);
                    const isActive = form.icona === name;
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setForm({ ...form, icona: name })}
                        title={name}
                        className={`p-2 rounded-lg border transition-colors ${
                          isActive
                            ? 'border-blue-500 bg-blue-50 text-blue-600'
                            : 'border-transparent text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <IconOption className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Colore</label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="color"
                    value={form.colore}
                    onChange={(e) => setForm({ ...form, colore: e.target.value })}
                    className="h-10 w-14 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={form.colore}
                    onChange={(e) => setForm({ ...form, colore: e.target.value.toLowerCase() })}
                    pattern="^#[0-9a-fA-F]{6}$"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>
                <div className="flex flex-wrap gap-1">
                  {COLOR_PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, colore: c })}
                      className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${
                        form.colore === c ? 'border-gray-900' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>
                {/* Preview */}
                <div className="mt-3">
                  <span className="text-xs text-gray-500">Anteprima:</span>
                  <span
                    className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ backgroundColor: `${form.colore}1A`, color: form.colore }}
                  >
                    <PreviewIcon className="h-3 w-3" />
                    {form.label || 'esempio'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contesti attivi
                </label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.attivo_lead}
                      onChange={(e) => setForm({ ...form, attivo_lead: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Lead
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.attivo_cliente}
                      onChange={(e) => setForm({ ...form, attivo_cliente: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Cliente
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.attivo_offerta}
                      onChange={(e) => setForm({ ...form, attivo_offerta: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Offerta
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.attivo_dashboard}
                      onChange={(e) => setForm({ ...form, attivo_dashboard: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Mostra nella dashboard
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
                  <input
                    type="number"
                    value={form.ordine}
                    onChange={(e) => setForm({ ...form, ordine: Number(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.attivo}
                      onChange={(e) => setForm({ ...form, attivo: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Attivo
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {form.id ? 'Salva' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          isOpen={!!deleteTarget}
          title="Elimina tipologia"
          message={`Eliminare la tipologia "${deleteTarget.label}"? Le attività esistenti con questo codice resteranno, ma non sarà più selezionabile nei form.`}
          confirmLabel="Elimina"
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onClose={() => setDeleteTarget(null)}
          isLoading={deleteMutation.isPending}
          variant="danger"
        />
      )}
    </div>
  );
}
